import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import { adminApi } from '@/lib/adminApi';
import type { AdminLog } from '@/lib/adminApi';
import { formatDate } from '@/lib/format';
import { AdminCard, EmptyBlock, ErrorBlock, LoadingBlock, TableCell, TableHeaderCell } from './ui';

const QUICK_FILTERS = ['', 'auth.', 'withdrawal.', 'client.', 'settings.', 'security.'] as const;

export default function LogsTab({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(
    async (value: string) => {
      setLoading(true);
      setFailed(false);
      try {
        const res = await adminApi.logsList(token, value);
        if (!res.ok) {
          setFailed(true);
          return;
        }
        setLogs((res.logs ?? []) as AdminLog[]);
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    load('');
  }, [load]);

  return (
    <AdminCard
      title={t('admin.logs.title')}
      subtitle={t('admin.logs.subtitle')}
      actions={
        <>
          <div className="inline-flex items-center p-1 rounded-full border border-background-300 bg-background-100 overflow-x-auto">
            {QUICK_FILTERS.map((value) => (
              <button
                key={value || 'all'}
                type="button"
                onClick={() => {
                  setSearch(value);
                  load(value);
                }}
                className={`h-7 px-2.5 rounded-full text-[11px] font-semibold font-label whitespace-nowrap transition-colors cursor-pointer ${
                  search === value
                    ? 'bg-background-50 text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                {value ? value.replace('.', '') : t('admin.common.all')}
              </button>
            ))}
          </div>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center justify-center w-4 h-4 text-foreground-400">
              <i className="ri-search-line text-sm" aria-hidden="true" />
            </span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') load(search.trim());
              }}
              placeholder={t('admin.logs.search')}
              className="h-9 w-full sm:w-48 box-border rounded-full border border-background-300 bg-background-50 pl-9 pr-3 text-[12.5px] text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-500 transition-colors"
            />
          </div>
          <Button size="sm" variant="outline" onClick={() => load(search.trim())} icon={<i className="ri-filter-3-line" aria-hidden="true" />}>
            {t('admin.logs.apply')}
          </Button>
        </>
      }
      padded={false}
    >
      {loading ? (
        <LoadingBlock />
      ) : failed ? (
        <ErrorBlock onRetry={() => load(search)} />
      ) : logs.length === 0 ? (
        <EmptyBlock label={t('admin.logs.empty')} icon="ri-history-line" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px]">
            <thead className="bg-background-100">
              <tr>
                <TableHeaderCell>{t('admin.logs.date')}</TableHeaderCell>
                <TableHeaderCell>{t('admin.logs.actor')}</TableHeaderCell>
                <TableHeaderCell>{t('admin.logs.action')}</TableHeaderCell>
                <TableHeaderCell>{t('admin.logs.target')}</TableHeaderCell>
                <TableHeaderCell>{t('admin.logs.ip')}</TableHeaderCell>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {logs.map((row) => (
                <tr key={row.id} className="hover:bg-background-100/60 transition-colors">
                  <TableCell>
                    <span className="text-foreground-500 whitespace-nowrap">
                      {formatDate(row.createdAt, i18n.language, true)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="block text-[12.5px] text-foreground-900">{row.actorLabel ?? '—'}</span>
                    <span className="block text-[10.5px] uppercase tracking-wide text-foreground-400 font-label">
                      {row.actorType}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-label text-[12px] text-foreground-800">{row.action}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-[12px] text-foreground-600 break-all">{row.target ?? '—'}</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-label text-[11.5px] text-foreground-600">{row.ip ?? '—'}</span>
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminCard>
  );
}